import { useWatch } from "react-hook-form";
import { useApplicationForm, useMLTAApplicationForm, useGIWLApplicationForm } from "hooks/useApplicationForm";
import { useProductContext } from "context/ProductContext";

/**
 * Owns the three product forms (DTO / MLTA / GIWL) and exposes the one matching
 * the active product, along with its watched card slices.
 *
 * All three `useForm`/`useWatch` calls run unconditionally to respect the rules
 * of hooks; `selectByProduct` then narrows to the active product. Centralizing
 * this keeps the product branching out of the page component.
 */
export const useProductForm = () => {
    const dtoForm = useApplicationForm();
    const mltaForm = useMLTAApplicationForm();
    const giwlForm = useGIWLApplicationForm();
    const { isDTO, isMLTA, isGIWL } = useProductContext();

    // Selects the value matching the current product type (GIWL > MLTA > DTO fallback)
    const selectByProduct = (dtoValue: any, mltaValue: any, giwlValue: any): any => {
        if (isGIWL) return giwlValue;
        if (isMLTA) return mltaValue;
        return dtoValue;
    };

    const form = selectByProduct(dtoForm, mltaForm, giwlForm);

    // Watch form fields per-product (hooks must be called unconditionally)
    const dtoContactInfo = useWatch({ control: dtoForm.control, name: "contactInformation" });
    const statementOfHealthData = useWatch({ control: dtoForm.control, name: "statementOfHealth" });
    const dtoCoverageReplacementData = useWatch({ control: dtoForm.control, name: "coverageReplacement" });
    const dtoPolicyBeneficiaryData = useWatch({ control: dtoForm.control, name: "policyBeneficiary" });
    const dtoSecondaryAddresseeData = useWatch({ control: dtoForm.control, name: "secondaryAddressee" });

    const mltaContactInfo = useWatch({ control: mltaForm.control, name: "contactInformation" });
    const mltaPolicyBeneficiaryData = useWatch({ control: mltaForm.control, name: "policyBeneficiary" });
    const mltaSecondaryAddresseeData = useWatch({ control: mltaForm.control, name: "secondaryAddressee" });

    const giwlContactInfo = useWatch({ control: giwlForm.control, name: "contactInformation" });
    const giwlPolicyBeneficiaryData = useWatch({ control: giwlForm.control, name: "policyBeneficiary" });
    const giwlSecondaryAddresseeData = useWatch({ control: giwlForm.control, name: "secondaryAddressee" });
    const giwlCoverageReplacementData = useWatch({ control: giwlForm.control, name: "coverageReplacement" });

    const contactInfo = selectByProduct(dtoContactInfo, mltaContactInfo, giwlContactInfo);
    const policyBeneficiaryData = selectByProduct(
        dtoPolicyBeneficiaryData,
        mltaPolicyBeneficiaryData,
        giwlPolicyBeneficiaryData
    );
    const secondaryAddresseeData = selectByProduct(
        dtoSecondaryAddresseeData,
        mltaSecondaryAddresseeData,
        giwlSecondaryAddresseeData
    );
    const coverageReplacementData = selectByProduct(dtoCoverageReplacementData, null, giwlCoverageReplacementData);

    return {
        // Forms
        form,
        giwlForm,
        // Product flags
        isDTO,
        isMLTA,
        isGIWL,
        // Watched card slices (already narrowed to the active product)
        contactInfo,
        statementOfHealthData,
        coverageReplacementData,
        policyBeneficiaryData,
        secondaryAddresseeData,
    };
};
